import { AnimatePresence, motion } from "motion/react";
import { useRef, useState } from "react";
import { usePersistentStore } from "@/stores/playerStore";

interface ArchivePanelProps {
  open: boolean;
  onClose: () => void;
  onBlockTag: (tag: string) => void;
}

function formatWatchTime(milliseconds: number) {
  const seconds = Math.floor(milliseconds / 1_000);
  if (seconds < 60) return `${seconds} sec`;
  const minutes = Math.floor(seconds / 60);
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes} min`;
}

export default function ArchivePanel({ open, onClose, onBlockTag }: ArchivePanelProps) {
  const stats = usePersistentStore((state) => state.watchStats);
  const blockedTags = usePersistentStore((state) => state.blockedTags);
  const unblockTag = usePersistentStore((state) => state.unblockTag);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragDistance = useRef(0);
  const dragStart = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const videoCount = Number.isFinite(stats.videoCount) ? stats.videoCount : 0;
  const watchTimeMs = Number.isFinite(stats.watchTimeMs) ? stats.watchTimeMs : 0;
  const tags = Object.entries(stats.tagCounts ?? {}).sort(([, a], [, b]) => b - a).slice(0, 24);
  const maxCount = tags[0]?.[1] ?? 1;

  return <AnimatePresence>{open && <>
    <motion.div className="absolute inset-0 bg-void/70 z-40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-ui-zone />
    <motion.section className="absolute right-0 top-0 bottom-0 w-full sm:w-[520px] overflow-y-auto z-[100] glass-panel archive-panel p-6 sm:p-8 safe-top safe-bottom" initial={{ opacity: 0, x: "100%" }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: "100%" }} transition={{ duration: 0.22 }} data-ui-zone>
      <header className="flex items-start justify-between gap-4">
        <div><div className="vfd text-lg">观影档案</div><p className="mt-1 text-xs text-[#65476e]">全部数据只保存在这台设备</p></div>
        <button onClick={onClose} className="text-xs font-medium text-[#4c3156] hover:text-signal">CLOSE [ESC]</button>
      </header>

      <div className="grid grid-cols-2 gap-3 mt-6"><Stat label="看过视频" value={`${videoCount}`} suffix="部" /><Stat label="观看时长" value={formatWatchTime(watchTimeMs)} /></div>

      <div className="mt-7">
        <div className="flex items-center justify-between gap-3"><div><h2 className="font-bold text-sm text-[#2f193a]">标签星球</h2><p className="text-[11px] text-[#65476e] mt-1">自由拖动平移；鼠标滚轮缩放；点击标签即可屏蔽。</p></div><span className="acg-sticker px-2.5 py-1 text-[10px] text-signal">{tags.length} TAGS</span></div>
        {tags.length > 0 ? (
          <div
            className="tag-orbit mt-4 cursor-grab active:cursor-grabbing"
            onWheel={(event) => { event.preventDefault(); setZoom((value) => Math.min(1.8, Math.max(0.65, value - event.deltaY * 0.001))); }}
            onPointerDown={(event) => {
              if (event.target instanceof Element && event.target.closest("button")) return;
              event.currentTarget.setPointerCapture(event.pointerId);
              dragDistance.current = 0;
              dragStart.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
            }}
            onPointerMove={(event) => {
              if (!dragStart.current) return;
              const x = Math.max(-180, Math.min(180, dragStart.current.panX + event.clientX - dragStart.current.x));
              const y = Math.max(-150, Math.min(150, dragStart.current.panY + event.clientY - dragStart.current.y));
              dragDistance.current = Math.abs(x - dragStart.current.panX) + Math.abs(y - dragStart.current.panY);
              setPan({ x, y });
            }}
            onPointerUp={(event) => {
              if (dragStart.current) event.currentTarget.releasePointerCapture(event.pointerId);
              dragStart.current = null;
              window.setTimeout(() => { dragDistance.current = 0; }, 0);
            }}
            aria-label="可拖动的标签星球"
          >
            <div className="tag-orbit-content" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}>
              <div className="tag-orbit-core">TAG<br />ORBIT</div>
              {tags.map(([tag, count], index) => {
                const angle = (index / tags.length) * Math.PI * 2 - Math.PI / 2;
                const radius = 28 + (index % 3) * 10;
                const blocked = blockedTags.includes(tag);
                return <button key={tag} onClick={() => { if (dragDistance.current < 8) blocked ? unblockTag(tag) : onBlockTag(tag); }} className={`tag-orbit-item ${blocked ? "is-blocked" : ""}`} style={{ left: `${50 + Math.cos(angle) * radius}%`, top: `${50 + Math.sin(angle) * radius}%`, fontSize: `${11 + Math.round((count / maxCount) * 8)}px` }} title={blocked ? `取消屏蔽 ${tag}` : `屏蔽 ${tag}`}>{tag}<small>×{count}</small></button>;
              })}
            </div>
          </div>
        ) : <div className="mt-4 py-10 rounded-2xl border border-[#d995b8] text-center text-sm text-[#65476e]">看几部视频后，这里会长出你的标签星球。</div>}
      </div>

      <div className="mt-7 pt-5 border-t border-[#d995b8]"><h2 className="font-bold text-sm text-[#2f193a]">已屏蔽标签</h2>{blockedTags.length > 0 ? <div className="flex flex-wrap gap-2 mt-3">{blockedTags.map((tag) => <button key={tag} onClick={() => unblockTag(tag)} className="rounded-full border border-[#b93172] bg-[#fff4fa] px-3 py-1.5 text-xs font-bold text-[#781f4d] hover:bg-signal hover:text-white transition-colors" title="点击取消屏蔽">{tag} ×</button>)}</div> : <p className="mt-2 text-xs text-[#65476e]">暂未屏蔽任何标签。</p>}</div>
    </motion.section>
  </>}</AnimatePresence>;
}

function Stat({ label, value, suffix = "" }: { label: string; value: string; suffix?: string }) {
  return <div className="rounded-2xl border border-[#d995b8] bg-white px-4 py-3 shadow-sm"><div className="text-[10px] tracking-widest2 text-[#65476e]">{label}</div><div className="mt-1 font-display text-xl font-bold text-[#26102f]">{value}<span className="ml-1 text-xs font-medium text-[#65476e]">{suffix}</span></div></div>;
}
